import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, AlertCircle, Info, CheckCircle } from 'lucide-react';

const OperationalAlertsCard = ({ alerts, isLoading }) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-40 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((item) => (
              <div key={item} className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-gray-200 rounded-full"></div>
                <div className="flex-1 h-4 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Handle the backend response structure which contains separate alert arrays
  const getAllAlerts = () => {
    if (!alerts) return [];
    
    const allAlerts = [];
    
    // Add critical alerts
    if (alerts.criticalAlerts && Array.isArray(alerts.criticalAlerts)) {
      allAlerts.push(...alerts.criticalAlerts.map(alert => ({ ...alert, severity: 'CRITICAL' })));
    }
    
    // Add warning alerts
    if (alerts.warningAlerts && Array.isArray(alerts.warningAlerts)) {
      allAlerts.push(...alerts.warningAlerts.map(alert => ({ ...alert, severity: 'WARNING' })));
    }
    
    // Add info alerts
    if (alerts.infoAlerts && Array.isArray(alerts.infoAlerts)) {
      allAlerts.push(...alerts.infoAlerts.map(alert => ({ ...alert, severity: 'INFO' })));
    }
    
    // Sort by timestamp (newest first)
    return allAlerts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const allAlerts = getAllAlerts();

  const getAlertColor = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'WARNING':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'INFO':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getAlertIcon = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'WARNING':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />;
      case 'INFO':
        return <Info className="w-4 h-4 text-blue-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {t('dashboard.operationalAlerts')}
        </h3>
        <AlertTriangle className="w-6 h-6 text-orange-500" />
      </div>

      {!alerts || allAlerts.length === 0 ? (
        <div className="text-center py-8">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <p className="text-gray-500">{t('dashboard.noAlertsMessage')}</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {allAlerts.map((alert, index) => (
            <div
              key={alert.id || index}
              className={`p-3 rounded-lg border ${getAlertColor(alert.severity)}`}
            >
              <div className="flex items-start space-x-3 rtl:space-x-reverse">
                {getAlertIcon(alert.severity)}
                <div className="flex-1">
                  <p className="text-sm font-medium">{alert.title}</p>
                  {alert.description && (
                    <p className="text-xs mt-1 opacity-75">{alert.description}</p>
                  )}
                  {alert.timestamp && (
                    <p className="text-xs mt-2 opacity-60">
                      {new Date(alert.timestamp).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {allAlerts.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
            {t('dashboard.viewAllAlerts')}
          </button>
        </div>
      )}
    </div>
  );
};

export default OperationalAlertsCard;
